import { useEffect, useState, useMemo } from 'react'
import { Card } from '@/components/Card'
import { fetchApi } from '@/lib/api'
import {
  RefreshCw,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Search,
  Clock,
  User,
  Calendar,
  AlertCircle,
  EyeOff,
  Eye,
} from 'lucide-react'
import { formatDate, formatDateTime } from '@/lib/date'

type Task = {
  id: string
  key: string
  subject: string
  description: string | null
  status_id: string
  status_name: string
  type_id: string
  type_name: string
  priority: number | null
  assignee_id: string | null
  assignee_name: string
  start_date: string | null
  due_date: string | null
  estimated_hours: number | null
  actual_hours: number | null
  progress: number | null
  parent_task_id: string | null
  category_id: string | null
  milestone_id: string | null
  label_ids: string[]
  created_by: string | null
  updated_by: string | null
  created_at: string | null
  updated_at: string | null
}

type TaskStatus = {
  id: string
  name: string
  color: string | null
  sort_order: number | null
  is_done: boolean | null
  is_completed: boolean | number | null
}

type TaskType = {
  id: string
  name: string
  color: string | null
  icon: string | null
}

type TaskComment = {
  id: string
  body?: string
  content?: string
  created_by: string
  created_by_name: string
  created_at: string
  updated_at: string
}

function priorityLabel(p: number | null): string {
  if (p === null || p === undefined) return '-'
  const labels: Record<number, string> = {
    1: 'Lowest',
    2: 'Low',
    3: 'Medium',
    4: 'High',
    5: 'Highest',
  }
  return labels[p] || `P${p}`
}

function priorityColor(p: number | null): string {
  if (!p) return 'text-[var(--color-text-muted)]'
  if (p >= 5) return 'text-red-400'
  if (p >= 4) return 'text-orange-400'
  if (p >= 3) return 'text-yellow-400'
  return 'text-[var(--color-text-muted)]'
}

function isStatusCompleted(status: TaskStatus | undefined): boolean {
  if (!status) return false
  return !!(status.is_completed || status.is_done)
}

function statusColor(status: TaskStatus | undefined, isDone: boolean | null): string {
  if (isDone) return 'bg-green-500/10 text-green-400'
  if (status?.color) {
    return `bg-[${status.color}]/10 text-[${status.color}]`
  }
  return 'bg-blue-500/10 text-blue-400'
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return formatDate(dateStr)
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false
  return new Date(dueDate) < new Date()
}

export function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([])
  const [statuses, setStatuses] = useState<TaskStatus[]>([])
  const [types, setTypes] = useState<TaskType[]>([])
  const [loading, setLoading] = useState(true)
  const [tasksLoading, setTasksLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [hideCompleted, setHideCompleted] = useState(true)
  const [expandedTask, setExpandedTask] = useState<string | null>(null)
  const [taskComments, setTaskComments] = useState<Record<string, TaskComment[]>>({})
  const [commentsLoading, setCommentsLoading] = useState<string | null>(null)
  const [pagination, setPagination] = useState<{ has_more?: boolean; next_cursor?: string }>({})
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const statusMap = new Map(statuses.map((s) => [s.id, s]))
  const typeMap = new Map(types.map((t) => [t.id, t]))

  const loadMetadata = async () => {
    try {
      const [statusData, typeData] = await Promise.all([
        fetchApi<{ statuses: TaskStatus[] }>('/twp/statuses').catch(() => ({ statuses: [] })),
        fetchApi<{ types: TaskType[] }>('/twp/types').catch(() => ({ types: [] })),
      ])
      setStatuses(statusData.statuses)
      setTypes(typeData.types)
    } catch {
      // Metadata is optional, continue without it
    }
  }

  const loadTasks = async (append = false) => {
    if (append) {
      setLoadingMore(true)
    } else {
      setTasksLoading(true)
    }
    setError(null)
    try {
      const params = new URLSearchParams({ limit: '50' })
      if (keyword) params.set('keyword', keyword)
      if (statusFilter) params.set('status_id', statusFilter)
      if (typeFilter) params.set('type_id', typeFilter)
      if (append && pagination.next_cursor) params.set('cursor', pagination.next_cursor)

      const data = await fetchApi<{ tasks: Task[]; pagination: { has_more?: boolean; next_cursor?: string } }>(
        `/twp/tasks?${params}`
      )

      if (append) {
        setTasks((prev) => [...prev, ...data.tasks])
      } else {
        setTasks(data.tasks)
      }
      setPagination(data.pagination || {})
    } catch (e) {
      if (!append) setTasks([])
      setError(e instanceof Error ? e.message : 'Failed to load tasks')
    } finally {
      setTasksLoading(false)
      setLoadingMore(false)
      setLoading(false)
    }
  }

  const loadComments = async (taskId: string) => {
    if (taskComments[taskId]) {
      setExpandedTask(expandedTask === taskId ? null : taskId)
      return
    }
    setExpandedTask(taskId)
    setCommentsLoading(taskId)
    try {
      const data = await fetchApi<{ comments: TaskComment[] }>(`/twp/tasks/${taskId}/comments`)
      setTaskComments((prev) => ({ ...prev, [taskId]: data.comments }))
    } catch {
      setTaskComments((prev) => ({ ...prev, [taskId]: [] }))
    } finally {
      setCommentsLoading(null)
    }
  }

  useEffect(() => {
    loadMetadata()
    loadTasks()
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    loadTasks()
  }

  const handleStatusChange = (value: string) => {
    setStatusFilter(value)
    // Trigger reload after state update
    setTimeout(() => loadTasks(), 0)
  }

  const handleTypeChange = (value: string) => {
    setTypeFilter(value)
    setTimeout(() => loadTasks(), 0)
  }

  // Client-side filtering for completed tasks
  const completedStatusIds = useMemo(
    () => new Set(statuses.filter((s) => isStatusCompleted(s)).map((s) => s.id)),
    [statuses]
  )

  const filteredTasks = useMemo(() => {
    if (!hideCompleted || completedStatusIds.size === 0) return tasks
    return tasks.filter((t) => !completedStatusIds.has(t.status_id))
  }, [tasks, hideCompleted, completedStatusIds])

  const isLoading = loading || tasksLoading

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">TWP Tasks</h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            TWP Project Tasks (Production)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {statuses.length > 0 && (
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
            >
              <option value="">All Statuses</option>
              {statuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          {types.length > 0 && (
            <select
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)]"
            >
              <option value="">All Types</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={() => setHideCompleted(!hideCompleted)}
            title={hideCompleted ? 'Show completed tasks' : 'Hide completed tasks'}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded border transition-colors ${
              hideCompleted
                ? 'border-blue-500/50 bg-blue-500/10 text-blue-400'
                : 'border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-muted)]'
            }`}
          >
            {hideCompleted ? <EyeOff size={14} /> : <Eye size={14} />}
            <span className="hidden sm:inline">{hideCompleted ? 'Hiding done' : 'Show all'}</span>
          </button>
          <button
            onClick={() => loadTasks()}
            disabled={isLoading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors"
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search tasks by keyword..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]"
          />
        </div>
        <button
          type="submit"
          disabled={tasksLoading}
          className="flex items-center gap-1 px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
        >
          {tasksLoading ? <RefreshCw size={14} className="animate-spin" /> : 'Search'}
        </button>
      </form>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Tasks list */}
      <Card
        title={`Tasks (${filteredTasks.length}${hideCompleted && filteredTasks.length < tasks.length ? ` / ${tasks.length}` : ''}${pagination.has_more ? '+' : ''})`}
      >
        {isLoading && tasks.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Loading...</p>
        ) : filteredTasks.length > 0 ? (
          <div className="space-y-1">
            {filteredTasks.map((task) => {
              const status = statusMap.get(task.status_id)
              const type = typeMap.get(task.type_id)
              const statusName = task.status_name || status?.name || '-'
              const typeName = task.type_name || type?.name || '-'
              const overdue = isOverdue(task.due_date) && !isStatusCompleted(status)

              return (
                <div key={task.id}>
                  <div
                    className="flex items-center justify-between p-3 rounded bg-[var(--color-bg)] cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors"
                    onClick={() => loadComments(task.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {expandedTask === task.id ? (
                          <ChevronDown size={14} className="shrink-0" />
                        ) : (
                          <ChevronRight size={14} className="shrink-0" />
                        )}
                        {task.key && (
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] shrink-0">
                            {task.key}
                          </span>
                        )}
                        <span className="text-sm font-medium truncate">{task.subject}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 ml-5 text-xs text-[var(--color-text-muted)]">
                        <span
                          className={`px-1.5 py-0.5 rounded ${statusColor(status, isStatusCompleted(status))}`}
                        >
                          {statusName}
                        </span>
                        {typeName !== '-' && (
                          <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-hover)]">
                            {typeName}
                          </span>
                        )}
                        {task.assignee_name && (
                          <span className="flex items-center gap-1">
                            <User size={11} />
                            {task.assignee_name}
                          </span>
                        )}
                        {task.due_date && (
                          <span className={`flex items-center gap-1 ${overdue ? 'text-red-400' : ''}`}>
                            <Calendar size={11} />
                            {fmtDate(task.due_date)}
                            {overdue && ' (overdue)'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      <div className="text-right text-xs">
                        <div className={priorityColor(task.priority)}>
                          {priorityLabel(task.priority)}
                        </div>
                        {(task.estimated_hours || task.actual_hours) && (
                          <div className="flex items-center gap-1 mt-0.5 text-[var(--color-text-muted)]">
                            <Clock size={11} />
                            {task.actual_hours ?? 0}/{task.estimated_hours ?? 0}h
                          </div>
                        )}
                      </div>
                      {task.progress !== null && task.progress !== undefined && (
                        <div className="w-12">
                          <div className="text-xs text-center text-[var(--color-text-muted)] mb-0.5">
                            {task.progress}%
                          </div>
                          <div className="h-1.5 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500 rounded-full transition-all"
                              style={{ width: `${Math.min(task.progress, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                      <a
                        href={`https://app.tocca.systems/v1/projects/tasks/${task.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>

                  {/* Expanded: task detail + comments */}
                  {expandedTask === task.id && (
                    <div className="mx-3 mb-2 p-3 rounded bg-[var(--color-bg)] border border-[var(--color-border)] space-y-3">
                      {/* Task description */}
                      {task.description && (
                        <div>
                          <div className="text-xs font-medium text-[var(--color-text-muted)] mb-1">
                            Description
                          </div>
                          <div className="text-sm whitespace-pre-wrap break-words">
                            {task.description}
                          </div>
                        </div>
                      )}

                      {/* Task metadata */}
                      <div className="border-t border-[var(--color-border)] pt-2 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
                        <div>
                          <span className="text-[var(--color-text-muted)]">Status: </span>
                          <span>{statusName}</span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Type: </span>
                          <span>{typeName}</span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Priority: </span>
                          <span className={priorityColor(task.priority)}>
                            {priorityLabel(task.priority)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Assignee: </span>
                          <span>{task.assignee_name || '-'}</span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Start: </span>
                          <span>{fmtDate(task.start_date)}</span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Due: </span>
                          <span className={overdue ? 'text-red-400' : ''}>
                            {fmtDate(task.due_date)}
                          </span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Est: </span>
                          <span>{task.estimated_hours ?? '-'}h</span>
                        </div>
                        <div>
                          <span className="text-[var(--color-text-muted)]">Actual: </span>
                          <span>{task.actual_hours ?? '-'}h</span>
                        </div>
                        {task.created_at && (
                          <div>
                            <span className="text-[var(--color-text-muted)]">Created: </span>
                            <span>{formatDateTime(task.created_at)}</span>
                          </div>
                        )}
                        {task.updated_at && (
                          <div>
                            <span className="text-[var(--color-text-muted)]">Updated: </span>
                            <span>{formatDateTime(task.updated_at)}</span>
                          </div>
                        )}
                      </div>

                      {/* Comments */}
                      <div className="border-t border-[var(--color-border)] pt-2">
                        <div className="text-xs font-medium text-[var(--color-text-muted)] mb-2">
                          Comments
                        </div>
                        {commentsLoading === task.id ? (
                          <p className="text-xs text-[var(--color-text-muted)]">
                            Loading comments...
                          </p>
                        ) : (taskComments[task.id]?.length ?? 0) > 0 ? (
                          <div className="space-y-2">
                            {taskComments[task.id].map((comment) => (
                              <div
                                key={comment.id}
                                className="p-2 rounded bg-[var(--color-bg-hover)] text-xs"
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">
                                    {comment.created_by_name || 'Unknown'}
                                  </span>
                                  <span className="text-[var(--color-text-muted)]">
                                    {formatDateTime(comment.created_at)}
                                  </span>
                                </div>
                                <div className="whitespace-pre-wrap break-words text-[var(--color-text)]">
                                  {comment.content || comment.body}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-[var(--color-text-muted)]">
                            No comments
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Load more */}
            {pagination.has_more && (
              <div className="pt-2 text-center">
                <button
                  onClick={() => loadTasks(true)}
                  disabled={loadingMore}
                  className="px-4 py-2 text-sm rounded bg-[var(--color-bg-hover)] hover:bg-[var(--color-border)] transition-colors disabled:opacity-50"
                >
                  {loadingMore ? (
                    <RefreshCw size={14} className="inline animate-spin mr-1" />
                  ) : null}
                  Load More
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-muted)]">
            No tasks found.{keyword ? ' Try adjusting the search keyword.' : ''}
          </p>
        )}
      </Card>
    </div>
  )
}
